import { useEffect, useLayoutEffect, useRef, useState } from "react";
import className from "classnames/bind";
import { useRouter } from "next/router";
import { Button } from "@components";
import ShareIcon from "@/assets/svg/share-03.svg";
import TickIcon from "@/assets/svg/tick-02.svg";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { canNativeShare } from "@/utils/share";

import styles from "./ShareButton.module.scss";
const cx = className.bind(styles);

type ShareButtonProps = {
  moduleSlug: string;
  className?: string;
  defaultLabel?: string;
  copiedLabel?: string;
  onShare?: () => Promise<boolean>;
};

const STATES = ["default", "copied"] as const;
type ShareState = (typeof STATES)[number];

// Fallbacks when the Global Labels (Digital Exhibit section) aren't set
const FALLBACK_LABELS: Record<ShareState, string> = {
  default: "Share this",
  copied: "Link copied",
};

const FALLBACK_MOBILE_DEFAULT = "Share";

const ICONS: Record<ShareState, React.FC<React.SVGProps<SVGSVGElement>>> = {
  default: ShareIcon,
  copied: TickIcon,
};

const COPIED_HOLD_MS = 2500;

// Share button — copies a query-param deep link to the module so the server can
// render its OG overrides. Uses the core Button for the treatment/hover; the
// only bespoke behavior is swapping the label + icon to "Link copied" on a
// successful copy (holding 2.5s), with the width tweening between the two
// states so the swap doesn't snap.
export default function ShareButton({
  moduleSlug,
  className: extraClass,
  defaultLabel,
  copiedLabel,
  onShare,
}: ShareButtonProps) {
  const router = useRouter();
  const { labels } = usePageWrapperData();
  // Copy from Global Labels (Digital Exhibit section), falling back to defaults;
  // per-instance overrides win (e.g. the quiz uses quizShareText/quizCopiedText)
  const shareLabels: Record<ShareState, string> = {
    default: defaultLabel || labels?.shareThis || FALLBACK_LABELS.default,
    copied: copiedLabel || labels?.shareCopied || FALLBACK_LABELS.copied,
  };
  // Short default label for narrow screens. A per-instance override (e.g. the
  // quiz) wins at every width — only the generic share copy shortens.
  const mobileDefaultLabel =
    defaultLabel || labels?.shareThisMobile || FALLBACK_MOBILE_DEFAULT;
  const [copied, setCopied] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [widths, setWidths] = useState<Record<ShareState, number> | null>(null);
  const measureRefs = useRef<Record<ShareState, HTMLSpanElement | null>>({
    default: null,
    copied: null,
  });
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Natural button widths drive the width tween; re-measure when fonts land and
  // on resize (the rem root re-bases across breakpoints)
  const measure = () => {
    const next = {} as Record<ShareState, number>;
    for (const state of STATES) {
      const el = measureRefs.current[state];
      const width = el ? Math.ceil(el.getBoundingClientRect().width) : 0;
      if (!(width > 0)) return;
      next[state] = width;
    }
    setWidths(next);
  };

  useLayoutEffect(() => {
    measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.fonts?.ready?.then(measure).catch(() => {});
    window.addEventListener("resize", measure);
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(motionQuery.matches);
    const onMotionChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    motionQuery.addEventListener?.("change", onMotionChange);
    return () => {
      motionQuery.removeEventListener?.("change", onMotionChange);
      window.removeEventListener("resize", measure);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default share action: the module deep link (renders OG overrides when opened)
  // goes to the native sheet where there is one, and to the clipboard otherwise.
  // Resolves true only for a copy — a native sheet is its own confirmation, so
  // the button stays in its default state.
  const shareModuleLink = async (): Promise<boolean> => {
    const path = router.asPath.split("?")[0];
    const url = `${window.location.origin}${path}?module=${moduleSlug}`;
    if (canNativeShare()) {
      try {
        await navigator.share({ title: document.title, url });
        return false;
      } catch (error) {
        // Dismissing the sheet is a decision, not a failure — copying behind the
        // user's back would contradict it. Anything else falls through to copy.
        if ((error as Error)?.name === "AbortError") return false;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      // Clipboard API unavailable (e.g. non-secure context) — textarea fallback
      try {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        return ok;
      } catch {
        return false;
      }
    }
  };

  const handleClick = async () => {
    const showCopied = await (onShare ?? shareModuleLink)();
    if (!showCopied) return;
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    setCopied(true);
    copiedTimer.current = setTimeout(() => setCopied(false), COPIED_HOLD_MS);
  };

  const state: ShareState = copied ? "copied" : "default";

  // Both default-label variants always render and CSS picks one per breakpoint,
  // so the markup is viewport-independent (the server has no screen size). The
  // measure copies render the same node, keeping the width tween accurate.
  const renderLabel = (s: ShareState) =>
    s === "copied" ? (
      shareLabels.copied
    ) : (
      <>
        <span className={cx("dx-share__label--mobile")}>{mobileDefaultLabel}</span>
        <span className={cx("dx-share__label--desktop")}>{shareLabels.default}</span>
      </>
    );

  return (
    <span
      className={cx("dx-share", { "dx-share--instant": reducedMotion }, extraClass)}
      style={widths ? { width: widths[state] } : undefined}
    >
      <Button
        className={cx("dx-share__btn")}
        type="button"
        onClick={handleClick}
        label={renderLabel(state)}
        ariaLabel={`${shareLabels.default} section`}
        icon={ICONS[state]}
      />

      {/* Offscreen copies of each state for natural-width measurement */}
      <span className={cx("dx-share__measure")} aria-hidden="true">
        {STATES.map((s) => (
          <span
            key={s}
            ref={(el) => {
              measureRefs.current[s] = el;
            }}
          >
            <Button type="button" label={renderLabel(s)} icon={ICONS[s]} />
          </span>
        ))}
      </span>
    </span>
  );
}
